import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, FormControl, Grid, TextField, Tooltip, Typography } from '@mui/material';
import { useGetBrandsQuery, useGetVehicleTypesQuery } from '../../../../api/graphql/generated/hooks';
import { ApolloError, CodetableSelect, EnumSelect, FilterDate } from '../../../../components';
import { DateFilter, VehicleCardFilter, VehicleCardStateEnum } from '../../../../api/graphql/generated/schema';
import { FilterDateType } from '../../../../components/common/FilterDate';
import { timeEndOfTheDay, timeStartOfTheDay, YesNoEnum } from '../../../../utils/const';
import { convertCardState } from '../../../../utils/graphGL/graphQLConverter';
import { CARD_STATE_ALL_ACTIVE } from '../../../../utils/graphGL/graphQLHelper';
import { VisibilityOutlined } from '@mui/icons-material';
import moment from 'moment';

const VC_STATE_ALL_ACTIVE: string = 'ALL_ACTIVE' as const;
const VC_ALL_STATES: string = '' as const;

const initState: VehicleCardFilter = Object.freeze({
  commission: '',
  optionCode: '',
  model: '',
  equipment: '',
  brand: [],
  vehicleType: [],
  state: [...CARD_STATE_ALL_ACTIVE],
  offerValidity: { from: '', to: '' },
  deliveryDate: { from: '', to: '' },
  updated: { from: '', to: '' },
  isInStoreCar: null,
});

const POSSIBLE_STATES = [
  VC_STATE_ALL_ACTIVE,
  VehicleCardStateEnum.InProgress,
  VehicleCardStateEnum.Approved,
  VehicleCardStateEnum.AwaitApproval,
  VehicleCardStateEnum.Published,
  VehicleCardStateEnum.Sold,
  VehicleCardStateEnum.Archived,
] as const;

const isInStoreCarPossibilities: Array<YesNoEnum> = [YesNoEnum.YES, YesNoEnum.NO];

const convertToDateFilter = (filterDate: FilterDateType | null | undefined, withTime?: boolean): FilterDateType => {
  if (!filterDate) {
    return { from: null, to: null };
  }
  return {
    from: resolveDate(filterDate.from, !!withTime ? timeStartOfTheDay : undefined),
    to: resolveDate(filterDate.to, !!withTime ? timeEndOfTheDay : undefined),
  };
};

const convertToFilterDateType = (filterDate: DateFilter, withTime?: boolean): FilterDateType => {
  return {
    from: resolveDate(filterDate.from, !!withTime ? timeStartOfTheDay : undefined),
    to: resolveDate(filterDate.to, !!withTime ? timeEndOfTheDay : undefined),
  };
};

const resolveDate = (inputDate: string | null | undefined, time: string | undefined) => {
  if (!inputDate) {
    return null;
  }

  const parsedDate = moment(inputDate);
  if (!parsedDate.isValid()) {
    return null;
  }

  if (time) {
    const [hours, minutes, seconds] = time.split(':').map(Number);
    parsedDate.set({ hour: hours, minute: minutes, second: seconds });
    return parsedDate.format('YYYY-MM-DDTHH:mm:ss');
  }

  return parsedDate.format('YYYY-MM-DD');
};

const convertToStateFiler = (cardStates: Array<string>): Array<VehicleCardStateEnum> | undefined => {
  let filterStates: Array<VehicleCardStateEnum> = [];
  if (cardStates.includes(VC_ALL_STATES)) {
    return undefined;
  }

  if (cardStates.length > 0) {
    cardStates.forEach((state) => {
      if (state === VC_STATE_ALL_ACTIVE) {
        filterStates.push(...CARD_STATE_ALL_ACTIVE);
      } else {
        filterStates.push(convertCardState(state));
      }
    });
  }

  return filterStates.length > 0 ? filterStates : undefined;
};

function fixSubmitFilter(filterParam: VehicleCardFilter): VehicleCardFilter {
  const filter: VehicleCardFilter = JSON.parse(JSON.stringify(filterParam));
  Object.keys(filter).forEach((key) => {
    if (typeof filter[key] === 'string' && filter[key] === '') {
      filter[key] = null;
    }
    if (['offerValidity', 'deliveryDate', 'updated'].includes(key)) {
      if (typeof filter[key].from === 'string' && filter[key].from === '') {
        filter[key].from = null;
      }
      if (typeof filter[key].to === 'string' && filter[key].to === '') {
        filter[key].to = null;
      }
    }
  });

  return filter;
}

const handleIsInStore = (
  appliedFilter: VehicleCardFilter,
  defaultValue: boolean | YesNoEnum | undefined | null,
): YesNoEnum | null | undefined => {
  if (appliedFilter) {
    if (appliedFilter.isInStoreCar === false) return YesNoEnum.NO;
    if (appliedFilter.isInStoreCar === true) return YesNoEnum.YES;
  }

  if (defaultValue !== null && defaultValue !== undefined) {
    if (defaultValue === false) return YesNoEnum.NO;
    if (defaultValue === true) return YesNoEnum.YES;
  }

  return defaultValue;
};

export default function VehicleCardOverviewFilter(props: {
  handleSubmitFilter: (filter: VehicleCardFilter) => void;
  appliedFilter: VehicleCardFilter;
}) {
  const { t } = useTranslation();
  const { appliedFilter } = props;

  const asteriskFilterTooltip = t('pages.vehicleCards.filters.tooltips.asterisk');

  const { loading: loadingBrands, data: gqlBrands, error: errorBrands } = useGetBrandsQuery();
  const { loading: loadingVehicleType, data: gqlVehicleType, error: errorVehicleType } = useGetVehicleTypesQuery();
  const [possibleStates] = React.useState<typeof POSSIBLE_STATES>(POSSIBLE_STATES);

  const [brands, setBrands] = React.useState<any>([]);
  const [vehicleType, setVehicleType] = React.useState<any>([]);
  const [commission, setCommission] = React.useState<VehicleCardFilter['commission']>(
    appliedFilter?.commission || initState.commission,
  );
  const [optionCode, setOptionCode] = React.useState<VehicleCardFilter['optionCode']>(
    appliedFilter?.optionCode || initState.optionCode,
  );
  const [brandIds, setBrandIds] = React.useState<VehicleCardFilter['brand']>(appliedFilter?.brand || initState.brand);
  const [vehicleTypeIds, setVehicleTypeIds] = React.useState<VehicleCardFilter['vehicleType']>(
    appliedFilter?.vehicleType || initState.vehicleType,
  );
  const [cardStates, setCardStates] = React.useState<VehicleCardFilter['state']>(
    appliedFilter?.state || initState.state,
  );
  const [model, setModel] = React.useState<VehicleCardFilter['model']>(appliedFilter?.model || initState.model);
  const [modelEquipment, setModelEquipment] = React.useState<VehicleCardFilter['equipment']>(
    appliedFilter?.equipment || initState.equipment,
  );
  const [offeredValidity, setOfferedValidity] = useState<FilterDateType | undefined | null>(
    (appliedFilter?.offerValidity && convertToFilterDateType(appliedFilter.offerValidity)) ?? initState.offerValidity,
  );
  const [deliveryDate, setDeliveryDate] = useState<FilterDateType | undefined | null>(
    (appliedFilter?.deliveryDate && convertToFilterDateType(appliedFilter.deliveryDate)) ?? initState.deliveryDate,
  );
  const [lastUpdated, setLastUpdated] = useState<FilterDateType | undefined | null>(
    (appliedFilter?.updated && convertToFilterDateType(appliedFilter.updated)) ?? initState.updated,
  );
  const [isInStoreCar, setIsInStoreCar] = useState<YesNoEnum | undefined | null>(
    handleIsInStore(appliedFilter, initState.isInStoreCar),
  );

  useEffect(() => {
    if (!loadingBrands && gqlBrands) {
      setBrands(gqlBrands.getBrands);
    }
  }, [loadingBrands, gqlBrands]);

  useEffect(() => {
    if (!loadingVehicleType && gqlVehicleType) {
      setVehicleType(gqlVehicleType.getVehicleTypes);
    }
  }, [loadingVehicleType, gqlVehicleType]);

  const resetFilter = () => {
    setCommission(initState.commission);
    setOptionCode(initState.optionCode);
    setBrandIds(initState.brand);
    setVehicleTypeIds(initState.vehicleType);
    setCardStates(initState.state);
    setOfferedValidity(initState.offerValidity);
    setDeliveryDate(initState.deliveryDate);
    setLastUpdated(initState.updated);
    setIsInStoreCar(null);
    setModelEquipment(initState.equipment);
    setModel(initState.model);

    props.handleSubmitFilter(fixSubmitFilter(initState));
  };

  const handleFilterCommission = (event: React.ChangeEvent<{ name?: string; value: string }>) => {
    setCommission(event.target.value);
  };

  const handleFilterOptionCode = (event: React.ChangeEvent<{ name?: string; value: string }>) => {
    setOptionCode(event.target.value);
  };
  const handleFilterModel = (event: React.ChangeEvent<{ name?: string; value: string }>) => {
    setModel(event.target.value);
  };
  const handleFilterModelEquipment = (event: React.ChangeEvent<{ name?: string; value: string }>) => {
    setModelEquipment(event.target.value);
  };

  const handleFilterOfferedValidity = (filterDate: FilterDateType) => {
    handleFilterDateUpdate(filterDate, setOfferedValidity);
  };

  const handleFilterDeliveryDate = (filterDate: FilterDateType) => {
    handleFilterDateUpdate(filterDate, setDeliveryDate);
  };

  const handleLastUpdated = (filterDate: FilterDateType) => {
    handleFilterDateUpdate(filterDate, setLastUpdated);
  };

  const handleFilterDateUpdate = (
    filterDate: FilterDateType | null | undefined,
    updateFunction: React.Dispatch<React.SetStateAction<FilterDateType | null | undefined>>,
  ) => {
    const newOV = {
      from: filterDate?.from,
      to: filterDate?.to,
    };
    updateFunction(newOV);
  };

  const handleSubmitFilter = () => {
    let isISC: boolean | undefined = undefined;
    if (isInStoreCar === YesNoEnum.YES) {
      isISC = true;
    } else if (isInStoreCar === YesNoEnum.NO) {
      isISC = false;
    }

    const filterToSubmit: VehicleCardFilter = {
      commission: commission ? commission : null,
      optionCode: optionCode ? optionCode : null,
      brand: brandIds,
      vehicleType: vehicleTypeIds,
      state: convertToStateFiler(cardStates as Array<string>),
      offerValidity: convertToDateFilter(offeredValidity),
      deliveryDate: convertToDateFilter(deliveryDate),
      updated: convertToDateFilter(lastUpdated, true),
      isInStoreCar: isISC,
      model: model ? model : null,
      equipment: modelEquipment ? modelEquipment : null,
    };
    props.handleSubmitFilter(fixSubmitFilter(filterToSubmit));
  };

  if (errorBrands) return <ApolloError error={[errorBrands]} />;
  if (errorVehicleType) return <ApolloError error={[errorVehicleType]} />;

  return (
    <Grid className='filter__holder'>
      <Box mb={3}>
        <Typography
          variant='h5'
          className='text-left'
        >
          {t('pages.vehicleCards.filters.title')}
        </Typography>
      </Box>
      <Grid
        container
        direction='row'
        justifyContent='flex-start'
        alignItems='center'
      >
        <Grid
          item
          xs={12}
          md={2}
          className='text-left'
        >
          <FormControl variant='outlined'>
            <Tooltip title={asteriskFilterTooltip}>
              <TextField
                id={'card-filter-commission'}
                label={t('fieldName.commission')}
                InputLabelProps={{
                  shrink: commission !== '',
                }}
                type={'text'}
                name={'commission'}
                value={commission}
                onChange={handleFilterCommission}
              />
            </Tooltip>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
          className='text-left'
        >
          <FormControl variant='outlined'>
            <Tooltip title={asteriskFilterTooltip}>
              <TextField
                id={'card-filter-option-code'}
                label={t('fieldName.optionCode')}
                type={'text'}
                name={'optionCode'}
                value={optionCode}
                InputLabelProps={{
                  shrink: optionCode !== '',
                }}
                onChange={handleFilterOptionCode}
              />
            </Tooltip>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
          className='text-left'
        >
          <EnumSelect
            allowClear
            inputValues={possibleStates}
            filteredValues={cardStates}
            setFilteredValues={setCardStates}
            label={t('fieldName.state')}
            enumLabelsPath='pages.vehicleCards.states'
            multiselect={true}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
          className='text-left'
        >
          <EnumSelect
            inputValues={isInStoreCarPossibilities}
            allowClear
            filteredValues={isInStoreCar}
            setFilteredValues={setIsInStoreCar}
            label={t('fieldName.inStoreCar')}
            multiselect={false}
            enumLabelsPath={'yesNoEnum'}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
          className='text-left'
        >
          <CodetableSelect
            inputValues={brands}
            filteredValues={brandIds || []}
            setFilteredValues={setBrandIds}
            label={t('fieldName.brand')}
            multiselect={true}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
          className='text-left'
        >
          <CodetableSelect
            inputValues={vehicleType}
            filteredValues={vehicleTypeIds || []}
            setFilteredValues={setVehicleTypeIds}
            label={t('fieldName.vehicleType')}
            multiselect={true}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
          className='text-left'
        >
          <FormControl variant='outlined'>
            <Tooltip title={asteriskFilterTooltip}>
              <TextField
                id={'card-filter-model'}
                label={t('fieldName.model')}
                type={'text'}
                name={'model'}
                value={model}
                InputLabelProps={{
                  shrink: model !== '',
                }}
                onChange={handleFilterModel}
              />
            </Tooltip>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
          className='text-left'
        >
          <FormControl variant='outlined'>
            <Tooltip title={asteriskFilterTooltip}>
              <TextField
                id={'card-filter-model-equipment'}
                label={t('fieldName.equipment')}
                type={'text'}
                name={'modelEquipment'}
                value={modelEquipment}
                InputLabelProps={{
                  shrink: modelEquipment !== '',
                }}
                onChange={handleFilterModelEquipment}
              />
            </Tooltip>
          </FormControl>
        </Grid>
      </Grid>
      <Grid
        container
        direction='row'
        justifyContent='flex-start'
        alignItems='center'
      >
        <Grid
          item
          xs={12}
          md={4}
          className='text-left'
        >
          <FilterDate
            inputDate={offeredValidity || { from: null, to: null }}
            labelCode={'fieldName.offerValidity'}
            handleUpdate={handleFilterOfferedValidity}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          className='text-left'
        >
          <FilterDate
            inputDate={deliveryDate || { from: null, to: null }}
            labelCode={'fieldName.deliveryDate'}
            handleUpdate={handleFilterDeliveryDate}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          className='text-left'
        >
          <FilterDate
            inputDate={lastUpdated || { from: null, to: null }}
            labelCode={'fieldName.lastUpdated'}
            handleUpdate={handleLastUpdated}
          />
        </Grid>
      </Grid>
      <Grid
        container
        direction='row'
        justifyContent='flex-start'
        alignItems='center'
      >
        <Grid
          item
          xs={12}
          md={12}
          className='text-right'
        >
          <Button
            className='reset-button'
            onClick={resetFilter}
          >
            {t('pages.vehicleCards.filters.resetButton')}
          </Button>
          <Button
            className='icon-button'
            onClick={handleSubmitFilter}
          >
            <VisibilityOutlined />
            {t('pages.vehicleCards.filters.searchButton')}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
}
